import { Suspense, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Clock, FileSearch } from "lucide-react";

import type { TimelineEntry } from "@/api/db/timeline";
import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { EvidenceModal } from "@/components/today/evidence-modal";
import type { ActionCard } from "@/types/actions";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { djs } from "@/lib/djs";
import { PessoaLabels, TimelineLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

export function Timeline({ personId }: { personId: number }) {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<ErrorBoundary fallback={<TimelineError />}>
				<Suspense fallback={<TimelineSkeleton />}>
					<TimelineData personId={personId} />
				</Suspense>
			</ErrorBoundary>
		</div>
	);
}

function TimelineError() {
	return <p className="text-sm text-destructive">{TimelineLabels.loadError}</p>;
}

function TimelineSkeleton() {
	return (
		<div className="flex flex-col gap-3">
			{Array.from({ length: 3 }, (_, i) => (
				<Skeleton key={i} className="h-16 w-full" />
			))}
		</div>
	);
}

function TimelineData({ personId }: { personId: number }) {
	const [evidence, setEvidence] = useState<{
		sources: ActionCard["sources"];
		title: string;
	} | null>(null);

	const { data } = useSuspenseQuery(
		orpc.timeline.forPerson.queryOptions({ input: { person_id: personId } }),
	);

	if (data.length === 0) {
		return <EmptyState icon={Clock} title={TimelineLabels.emptyTitle} />;
	}

	return (
		<>
			<div
				role="region"
				aria-label={PessoaLabels.side.timeline}
				tabIndex={0}
				className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [scrollbar-width:thin]"
			>
				<TimelineList events={data} onEvidence={setEvidence} />
			</div>

			{!!evidence && (
				<EvidenceModal
					personId={personId}
					sources={evidence.sources}
					title={evidence.title}
					onClose={() => setEvidence(null)}
				/>
			)}
		</>
	);
}

function TimelineList({
	events,
	onEvidence,
}: {
	events: TimelineEntry[];
	onEvidence: (value: { sources: ActionCard["sources"]; title: string }) => void;
}) {
	return (
		<ol className="flex flex-col" data-component="timeline">
			{events.map((event, i) => (
				<TimelineRow
					key={`${event.when}:${event.kind}:${event.source}:${event.code}:${i}`}
					event={event}
					onEvidence={onEvidence}
				/>
			))}
		</ol>
	);
}

function TimelineRow({
	event,
	onEvidence,
}: {
	event: TimelineEntry;
	onEvidence: (value: { sources: ActionCard["sources"]; title: string }) => void;
}) {
	const title = TimelineLabels.title(event);
	const metadata = TimelineLabels.metadata(event);
	const when = djs(event.when).locale("pt-br");
	const sources =
		event.action_sources ?? event.evidence_ids.map((id) => ({ kind: "message" as const, id }));

	return (
		<li className="flex gap-3 last:[&>div]:pb-1">
			<time className="w-14 shrink-0 pt-3 text-right font-mono text-xs leading-tight tabular-nums text-muted-foreground">
				{when.format("DD MMM")}
				<br />
				{when.format("YYYY")}
			</time>

			<div className="relative min-w-0 flex-1 border-l border-border pb-4 pl-5 pt-3">
				<span
					className={cn(
						"absolute left-0 top-3.5 size-2.5 -translate-x-1/2 rounded-full border-2 bg-background",
						TimelineLabels.dotTone(event),
					)}
				/>

				<p className="text-sm font-medium text-foreground">{title}</p>

				{!!metadata && <p className="mt-0.5 text-xs text-muted-foreground">{metadata}</p>}

				{!!event.detail && (
					<p className="mt-1 text-sm leading-snug text-muted-foreground">{event.detail}</p>
				)}

				{(!!event.author || sources.length > 0) && (
					<div className="mt-1.5 flex items-center gap-3">
						{!!event.author && (
							<span className="text-xs text-muted-foreground">
								{TimelineLabels.byAuthor(event.author)}
							</span>
						)}

						{sources.length > 0 && (
							<button
								type="button"
								onClick={() => onEvidence({ sources, title })}
								className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
							>
								<FileSearch className="size-3.5" />
								{TimelineLabels.evidence}
							</button>
						)}
					</div>
				)}
			</div>
		</li>
	);
}
